import { toast } from 'react-toastify';
import React from 'react';

// Helper function to extract and format API errors
export const handleApiError = (error, customMessage = null) => {
  console.error('API Error:', error);

  // If custom message is provided, use it
  if (customMessage) {
    toast.error(customMessage);
    return;
  }

  // Extract error from response
  let errorMessage = 'Възникна неочаквана грешка. Моля, опитайте отново.';
  let errors = [];
  let warnings = [];

  if (error.response?.data) {
    const data = error.response.data;
    
    // Handle validation errors with errors/warnings structure
    if (data.errors || data.warnings) {
      errors = data.errors || [];
      warnings = data.warnings || [];
      
      if (errors.length > 0) {
        // Show the first error as main message
        errorMessage = getHumanReadableMessage(errors[0]);
        
        // If there are multiple errors, show count
        if (errors.length > 1) {
          errorMessage += ` (и още ${errors.length - 1} ${errors.length - 1 === 1 ? 'грешка' : 'грешки'})`;
        }
      } else if (warnings.length > 0) {
        // If only warnings, show as warning toast
        const warningMessage = getHumanReadableMessage(warnings[0]);
        toast.warning(warningMessage);
        return;
      }
    }
    // Handle simple message structure
    else if (data.message) {
      errorMessage = translateCommonMessages(data.message);
    }
    // Handle string error
    else if (typeof data === 'string') {
      errorMessage = translateCommonMessages(data);
    }
  }
  // Handle network errors
  else if (error.message) {
    if (error.message.includes('Network Error')) {
      errorMessage = 'Няма връзка със сървъра. Моля, проверете интернет връзката си.';
    } else if (error.message.includes('timeout')) {
      errorMessage = 'Заявката отне твърде много време. Моля, опитайте отново.';
    } else {
      errorMessage = 'Възникна техническа грешка. Моля, опитайте отново.';
    }
  }

  showEnhancedToast('error', errorMessage);
};

// Helper function to show success messages
export const handleApiSuccess = (message, data = null) => {
  let successMessage = message;

  // Handle different success scenarios
  if (data?.message) {
    successMessage = translateCommonMessages(data.message);
  }

  showEnhancedToast('success', successMessage);
};

// Helper function to show validation errors in a user-friendly way
export const showValidationErrors = (errors, warnings = []) => {
  if (errors && errors.length > 0) {
    const mainError = getHumanReadableMessage(errors[0]);
    
    if (errors.length === 1) {
      showEnhancedToast('error', mainError, 'Грешка при валидация');
    } else {
      showEnhancedToast('error', `${mainError} (и още ${errors.length - 1} ${errors.length - 1 === 1 ? 'грешка' : 'грешки'})`, 'Множество грешки');
    }
  }

  if (warnings && warnings.length > 0) {
    warnings.forEach(warning => {
      showEnhancedToast('warning', getHumanReadableMessage(warning), 'Предупреждение');
    });
  }
};

// Translate common API messages to Bulgarian
const translateCommonMessages = (message) => {
  const translations = {
    'Validation failed': 'Валидацията не премина',
    'Unauthorized': 'Нямате права за тази операция',
    'Forbidden': 'Достъпът е забранен',
    'Not Found': 'Ресурсът не е намерен',
    'Internal Server Error': 'Вътрешна грешка на сървъра',
    'Bad Request': 'Невалидна заявка',
    'Conflict': 'Конфликт с данните',
    'Leave balances generated successfully': 'Балансите за отпуск са генерирани успешно',
    'Carry-over process completed successfully': 'Процесът за пренасяне е завършен успешно',
    'Leave request created successfully': 'Заявката за отпуск е създадена успешно',
    'Leave request updated successfully': 'Заявката за отпуск е актуализирана успешно',
    'Leave request approved successfully': 'Заявката за отпуск е одобрена успешно',
    'Leave request rejected successfully': 'Заявката за отпуск е отхвърлена успешно'
  };

  return translations[message] || message;
};

// Get human readable message (reuse from ValidationMessages)
const getHumanReadableMessage = (item) => {
  const { code, message } = item;
  
  const customMessages = {
    'INSUFFICIENT_BALANCE': (msg) => {
      const match = msg.match(/Requested: (\d+) days, Available: (\d+) days/);
      if (match) {
        return `Заявявате ${match[1]} дни, но имате само ${match[2]} дни налични.`;
      }
      return 'Нямате достатъчно дни отпуск за тази заявка.';
    },
    
    'OVERLAP': () => 'Избраният период се припокрива с друга ваша заявка за отпуск.',
    
    'PAST_DATE': () => 'Не можете да заявите отпуск за минали дати.',
    
    'SHORT_NOTICE': () => 'Препоръчваме да подавате заявки с повече предизвестие.',
    
    'TOO_CLOSE': () => 'Имате друга заявка много близо до тези дати.',
    
    'HIGH_USAGE': (msg) => {
      const match = msg.match(/(\d+) of your (\d+) available days/);
      if (match) {
        return `Тази заявка ще използва ${match[1]} от ${match[2]} налични дни.`;
      }
      return 'Тази заявка ще използва голяма част от наличните ви дни отпуск.';
    },
    
    'LARGE_REQUEST': (msg) => {
      const match = msg.match(/(\d+) days at once/);
      if (match) {
        return `Заявявате ${match[1]} дни наведнъж. Обмислете разделяне на по-малки периоди.`;
      }
      return 'Заявявате много дни наведнъж.';
    },
    
    'TOO_MANY_DAYS': () => 'Заявявате твърде много последователни дни отпуск.',
    
    'INVALID_DAYS': () => 'Броят дни трябва да е по-голям от нула.',
    
    'EMPLOYEE_INACTIVE': () => 'Вашият профил не е активен. Свържете се с HR отдела.',
    
    'INVALID_LEAVE_TYPE': () => 'Избраният тип отпуск не е наличен.',
    
    'NO_BALANCE': () => 'Няма настроен баланс за отпуск. Свържете се с HR отдела.',

    'CANNOT_MODIFY': () => 'Заявката не може да бъде променяна в текущото си състояние.',
    
    'REQUEST_NOT_FOUND': () => 'Заявката не е намерена в системата.',
    
    'VALIDATION_ERROR': () => 'Възникна грешка при проверката. Опитайте отново.'
  };
  
  const customMessage = customMessages[code];
  if (customMessage) {
    return typeof customMessage === 'function' ? customMessage(message) : customMessage;
  }
  
  return translateCommonMessages(message);
};

// Enhanced toast function with better styling and icons
const showEnhancedToast = (type, message, title = null) => {
  const toastContent = (
    <div className="toast-message">
      {title && <span className="toast-message-title">{title}</span>}
      <span className="toast-message-details">{message}</span>
    </div>
  );

  const options = {
    className: `validation-${type}-toast`,
    autoClose: type === 'error' ? 7000 : 5000, // Errors stay longer
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true
  };

  switch (type) {
    case 'success':
      toast.success(toastContent, options);
      break;
    case 'error':
      toast.error(toastContent, options);
      break;
    case 'warning':
      toast.warning(toastContent, options);
      break;
    case 'info':
      toast.info(toastContent, options);
      break;
    default:
      toast(toastContent, options);
  }
};

export default {
  handleApiError,
  handleApiSuccess,
  showValidationErrors,
  showEnhancedToast
};
